/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {getSymbolIterator, looseIdentical} from '../util';
/**
 * @param {?} a
 * @param {?} b
 * @return {?}
 */
export function devModeEqual(a: any, b: any): boolean {
  const /** @type {?} */ isListLikeIterableA = isListLikeIterable(a);
  const /** @type {?} */ isListLikeIterableB = isListLikeIterable(b);
  if (isListLikeIterableA && isListLikeIterableB) {
    return areIterablesEqual(a, b, devModeEqual);
  } else {
    const /** @type {?} */ isAObject = a && (typeof a === 'object' || typeof a === 'function');
    const /** @type {?} */ isBObject = b && (typeof b === 'object' || typeof b === 'function');
    if (!isListLikeIterableA && isAObject && !isListLikeIterableB && isBObject) {
      return true;
    } else {
      return looseIdentical(a, b);
    }
  }
}
/**
 * Indicates that the result of a {\@link Pipe} transformation has changed even though the
 * reference
 * has not changed.
 * 
 * The wrapped value will be unwrapped by change detection, and the unwrapped value will be stored.
 * 
 * Example:
 * 
 * ```
 * if (this._latestValue === this._latestReturnedValue) {
 *    return this._latestReturnedValue;
 *  } else {
 *    this._latestReturnedValue = this._latestValue;
 *    return WrappedValue.wrap(this._latestValue); // this will force update
 *  }
 * ```
 * \@stable
 */
export class WrappedValue {
/**
 * @param {?} wrapped
 */
constructor(public wrapped: any) {}
/**
 * @param {?} value
 * @return {?}
 */
static wrap(value: any): WrappedValue { return new WrappedValue(value); }
}

function WrappedValue_tsickle_Closure_declarations() {
/** @type {?} */
WrappedValue.prototype.wrapped;
}

/**
 * Helper class for unwrapping WrappedValue s
 */
export class ValueUnwrapper {
public hasWrappedValue = false;
/**
 * @param {?} value
 * @return {?}
 */
unwrap(value: any): any {
    if (value instanceof WrappedValue) {
      this.hasWrappedValue = true;
      return value.wrapped;
    }
    return value;
  }
/**
 * @return {?}
 */
reset() { this.hasWrappedValue = false; }
}

function ValueUnwrapper_tsickle_Closure_declarations() {
/** @type {?} */
ValueUnwrapper.prototype.hasWrappedValue;
}

/**
 * Represents a basic change from a previous to a new value.
 * \@stable
 */
export class SimpleChange {
/**
 * @param {?} previousValue
 * @param {?} currentValue
 * @param {?} firstChange
 */
constructor(public previousValue: any,
public currentValue: any,
public firstChange: boolean) {}
/**
 * Check whether the new value is the first value assigned.
 * @return {?}
 */
isFirstChange(): boolean { return this.firstChange; }
}

function SimpleChange_tsickle_Closure_declarations() {
/** @type {?} */
SimpleChange.prototype.previousValue;
/** @type {?} */
SimpleChange.prototype.currentValue;
/** @type {?} */
SimpleChange.prototype.firstChange;
}

/**
 * @param {?} obj
 * @return {?}
 */
export function isListLikeIterable(obj: any): boolean {
  if (!isJsObject(obj)) return false;
  return Array.isArray(obj) ||
      (!(obj instanceof Map) &&      // JS Map are iterables but return entries as [k, v]
       getSymbolIterator() in obj);  // JS Iterable have a Symbol.iterator prop
}
/**
 * @param {?} a
 * @param {?} b
 * @param {?} comparator
 * @return {?}
 */
export function areIterablesEqual(
    a: any, b: any, comparator: (a: any, b: any) => boolean): boolean {
  const /** @type {?} */ iterator1 = a[getSymbolIterator()]();
  const /** @type {?} */ iterator2 = b[getSymbolIterator()]();

  while (true) {
    const /** @type {?} */ item1 = iterator1.next();
    const /** @type {?} */ item2 = iterator2.next();
    if (item1.done && item2.done) return true;
    if (item1.done || item2.done) return false;
    if (!comparator(item1.value, item2.value)) return false;
  }
}
/**
 * @param {?} obj
 * @param {?} fn
 * @return {?}
 */
export function iterateListLike(obj: any, fn: (p: any) => any) {
  if (Array.isArray(obj)) {
    for (let /** @type {?} */ i = 0; i < obj.length; i++) {
      fn(obj[i]);
    }
  } else {
    const /** @type {?} */ iterator = obj[getSymbolIterator()]();
    let /** @type {?} */ item: any;
    while (!((item = iterator.next()).done)) {
      fn(item.value);
    }
  }
}
/**
 * @param {?} o
 * @return {?}
 */
export function isJsObject(o: any): boolean {
  return o !== null && (typeof o === 'function' || typeof o === 'object');
}



// WEBPACK FOOTER //
// ../../../../packages/core/src/change_detection/change_detection_util.ts